<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { isPhoneE164 } from 'shared/helpers/Validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import PageHeader from '../../SettingsSubPageHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DialingRulesEditor from './DialingRulesEditor.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const state = reactive({
  inboxName: '',
  phoneNumber: '',
  trunkHost: '',
  trunkPort: '5060',
  trunkUser: '',
  trunkPassword: '',
  codecs: 'ulaw,alaw,g729',
  dialingRules: [],
});

const uiFlags = useMapGetter('inboxes/getUIFlags');

const validationRules = {
  phoneNumber: { required, isPhoneE164 },
  trunkHost: { required },
  trunkUser: { required },
  trunkPassword: { required },
};

const v$ = useVuelidate(validationRules, state);
const isSubmitDisabled = computed(() => v$.value.$invalid);

const formErrors = computed(() => ({
  phoneNumber: v$.value.phoneNumber?.$error
    ? t('INBOX_MGMT.ADD.VOICE.PHONE_NUMBER.ERROR')
    : '',
  trunkHost: v$.value.trunkHost?.$error
    ? t('INBOX_MGMT.ADD.VOICE.ASTERISK.TRUNK_HOST.REQUIRED')
    : '',
  trunkUser: v$.value.trunkUser?.$error
    ? t('INBOX_MGMT.ADD.VOICE.ASTERISK.TRUNK_USER.REQUIRED')
    : '',
  trunkPassword: v$.value.trunkPassword?.$error
    ? t('INBOX_MGMT.ADD.VOICE.ASTERISK.TRUNK_PASSWORD.REQUIRED')
    : '',
}));

async function createChannel() {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  try {
    const channel = await store.dispatch('inboxes/createVoiceChannel', {
      name: state.inboxName.trim() || `Voice (${state.phoneNumber})`,
      voice: {
        phone_number: state.phoneNumber,
        provider: 'asterisk',
        provider_config: {
          trunk_host: state.trunkHost,
          trunk_port: Number(state.trunkPort) || 5060,
          trunk_user: state.trunkUser,
          trunk_password: state.trunkPassword,
          codecs: state.codecs
            .split(',')
            .map(c => c.trim())
            .filter(Boolean),
          dialing_rules: state.dialingRules,
        },
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: channel.id },
    });
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        t('INBOX_MGMT.ADD.VOICE.API.ERROR_MESSAGE')
    );
  }
}
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <PageHeader
      :header-title="t('INBOX_MGMT.ADD.VOICE.TITLE')"
      :header-content="t('INBOX_MGMT.ADD.VOICE.DESC')"
    />

    <form class="flex flex-col gap-5 mx-0" @submit.prevent="createChannel">
      <Input
        v-model="state.inboxName"
        :label="t('INBOX_MGMT.ADD.VOICE.INBOX_NAME.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.VOICE.INBOX_NAME.PLACEHOLDER')"
      />

      <Input
        v-model="state.phoneNumber"
        :label="t('INBOX_MGMT.ADD.VOICE.PHONE_NUMBER.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.VOICE.PHONE_NUMBER.PLACEHOLDER')"
        :message="formErrors.phoneNumber"
        :message-type="formErrors.phoneNumber ? 'error' : 'info'"
        @blur="v$.phoneNumber?.$touch"
      />

      <div class="rounded-xl border border-n-weak p-4 flex flex-col gap-4">
        <p class="text-sm font-semibold text-n-slate-12">
          {{ t('INBOX_MGMT.ADD.VOICE.ASTERISK.TRUNK_SECTION') }}
        </p>

        <div class="grid grid-cols-3 gap-3">
          <div class="col-span-2">
            <Input
              v-model="state.trunkHost"
              :label="t('INBOX_MGMT.ADD.VOICE.ASTERISK.TRUNK_HOST.LABEL')"
              placeholder="sip.seuvoip.com.br"
              :message="formErrors.trunkHost"
              :message-type="formErrors.trunkHost ? 'error' : 'info'"
              @blur="v$.trunkHost?.$touch"
            />
          </div>
          <Input
            v-model="state.trunkPort"
            :label="t('INBOX_MGMT.ADD.VOICE.ASTERISK.TRUNK_PORT.LABEL')"
            placeholder="5060"
            type="number"
          />
        </div>

        <div class="grid grid-cols-2 gap-3">
          <Input
            v-model="state.trunkUser"
            :label="t('INBOX_MGMT.ADD.VOICE.ASTERISK.TRUNK_USER.LABEL')"
            :placeholder="
              t('INBOX_MGMT.ADD.VOICE.ASTERISK.TRUNK_USER.PLACEHOLDER')
            "
            :message="formErrors.trunkUser"
            :message-type="formErrors.trunkUser ? 'error' : 'info'"
            @blur="v$.trunkUser?.$touch"
          />
          <Input
            v-model="state.trunkPassword"
            type="password"
            :label="t('INBOX_MGMT.ADD.VOICE.ASTERISK.TRUNK_PASSWORD.LABEL')"
            :placeholder="
              t('INBOX_MGMT.ADD.VOICE.ASTERISK.TRUNK_PASSWORD.PLACEHOLDER')
            "
            :message="formErrors.trunkPassword"
            :message-type="formErrors.trunkPassword ? 'error' : 'info'"
            @blur="v$.trunkPassword?.$touch"
          />
        </div>

        <Input
          v-model="state.codecs"
          :label="t('INBOX_MGMT.ADD.VOICE.ASTERISK.CODECS.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.VOICE.ASTERISK.CODECS.PLACEHOLDER')"
          :message="t('INBOX_MGMT.ADD.VOICE.ASTERISK.CODECS.HINT')"
          message-type="info"
        />
      </div>

      <div class="rounded-xl border border-n-weak p-4">
        <DialingRulesEditor v-model="state.dialingRules" />
      </div>

      <div>
        <NextButton
          :is-loading="uiFlags.isCreating"
          :disabled="isSubmitDisabled"
          :label="t('INBOX_MGMT.ADD.VOICE.SUBMIT_BUTTON')"
          type="submit"
        />
      </div>
    </form>
  </div>
</template>
